import { Injectable, inject, signal } from "@angular/core";
import { InstituteDepartment, UpdateInstituteDepartmentRequest } from "../interfaces/institute-department";
import { Institute } from "../interfaces/institute";
import { HttpClient, HttpContext, HttpErrorResponse, HttpResponse } from "@angular/common/http";
import { MITPService } from "./mitp.service";
import { lastValueFrom, map, Observable } from "rxjs";
import { IMFAValidateCode } from "../interfaces/imfavalidate-code";
import { IMFABackupCodes } from "../interfaces/imfabackup-codes";
import { IUserConfig } from "../interfaces/iuser-config";
import { EAuthorProtocolsView } from "../enums/protocols/eauthor-protocols-view";
import { Department } from "../interfaces/department";
import { User } from "../interfaces/users/user";
import { AuthCode } from "../entities/auth-code";
import { PublicUser } from "../interfaces/users/public-user";
import { Tag } from "../interfaces/tag";
import { TranslateService } from "@tolgee/ngx";
import { UserFavorite } from "../interfaces/users/user-favorite";
import { SKIP_ERROR_TOAST } from "./http-context-tokens";

/** Backend error key for a password that passes the length rule but is too weak or has leaked. */
export const PASSWORD_NOT_SAFE_KEY = "exception.password.not-safe";

@Injectable({
	providedIn: "root",
})
export class UserService {
	private httpClient = inject(HttpClient);
	private mitp = inject(MITPService);
	private translate = inject(TranslateService);

	readonly currentUser = signal<User | null>(this.buildUserFromSession());

	private readonly _ribbonCollapsed = signal<boolean>(sessionStorage.getItem("ribbonCollapsed") === "true");
	/** Whether the user collapsed the ribbon, from their config (cached in the session for reloads). */
	readonly ribbonCollapsed = this._ribbonCollapsed.asReadonly();

	loadCurrentUser(): void {
		this.httpClient.get<{ profil: User }>(this.mitp.Users() + "/my").subscribe((response) => {
			this.currentUser.set(response.profil);
		});
	}

	private buildUserFromSession(): User | null {
		const firstname = sessionStorage.getItem("firstname");
		const lastname = sessionStorage.getItem("lastname");
		if (!firstname && !lastname) return null;
		return { firstname: firstname ?? "", lastname: lastname ?? "", job: null } as unknown as User;
	}

	getPasskeyCreationOptions(userId: number): Observable<PasskeyCreationOptionsResponse> {
		return this.httpClient.post<PasskeyCreationOptionsResponse>(
			this.mitp.Users() + `/${userId}/passkeys/options`,
			{}
		);
	}

	registerPasskey(
		userId: number,
		payload: PasskeyRegistrationPayload
	): Observable<{ status: string; errorMessage?: string }> {
		return this.httpClient.post<{ status: string; errorMessage?: string }>(
			this.mitp.Users() + `/${userId}/passkeys/register`,
			payload
		);
	}

	getMyManagers(): Observable<User[]> {
		return this.httpClient.get<User[]>(this.mitp.Users() + "/managers/my");
	}

	getMyFavorites(): Observable<UserFavorite[]> {
		return this.httpClient.get<UserFavorite[]>(this.mitp.Users() + "/my/favorites");
	}

	getMyInstituteDepartment(): Observable<InstituteDepartment | null> {
		return this.httpClient.get<InstituteDepartment | null>(this.mitp.Users() + "/my/institute-department");
	}

	getMyDepartments(): Observable<Department[]> {
		return this.httpClient
			.get<{ profil: User }>(this.mitp.Users() + "/my")
			.pipe(map((response) => response.profil.departments ?? []));
	}

	addProtocolToFavorites(protocolId: number): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(this.mitp.Users() + `/my/favorites/protocols/${protocolId}`, null, {
			observe: "response",
		});
	}

	removeProtocolFromFavorites(protocolId: number): Observable<HttpResponse<null>> {
		return this.httpClient.delete<null>(this.mitp.Users() + `/my/favorites/protocols/${protocolId}`, {
			observe: "response",
		});
	}

	updateMyInstituteDepartment(payload: UpdateInstituteDepartmentRequest): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(this.mitp.Users() + "/my/institute-department", payload, {
			observe: "response",
		});
	}

	getMFAQrCode(): Observable<string> {
		return this.httpClient.get<string>(this.mitp.Users() + `/${this.getUserId()}/mfa/qr`);
	}

	checkMFA(code: string): Observable<IMFAValidateCode> {
		return this.httpClient.post<IMFAValidateCode>(this.mitp.Users() + `/${this.getUserId()}/mfa/validate`, {
			code,
		});
	}

	getMFABackupCodes(): Observable<IMFABackupCodes> {
		return this.httpClient.get<IMFABackupCodes>(this.mitp.Users() + `/${this.getUserId()}/mfa/backup`);
	}

	enableMFA(): Observable<HttpResponse<null>> {
		return this.httpClient.post<null>(this.mitp.Users() + `/${this.getUserId()}/mfa/enable`, null, {
			observe: "response",
		});
	}

	disableMFA(userId: number = null): Observable<HttpResponse<null>> {
		const id = userId == null ? this.getUserId() : userId;
		return this.httpClient.delete<null>(this.mitp.Users() + `/${id}/mfa/disable`, {
			observe: "response",
		});
	}

	transferUser(email: string, departments: Department[]): Observable<User> {
		return this.httpClient.post<User>(this.mitp.Users() + "/transfer", { email, departments });
	}

	getSecureJwt(code: string) {
		return this.httpClient.post(this.mitp.Users() + `/${this.getUserId()}/mfa/secure/code`, { code });
	}

	getSecureJwtByIp() {
		return this.httpClient.post(this.mitp.Users() + `/${this.getUserId()}/mfa/secure/ip`, null);
	}

	getUserId(): number {
		return sessionStorage.getItem("id") == null ? -1 : parseInt(sessionStorage.getItem("id"));
	}

	getUserEmail(): string {
		return sessionStorage.getItem("email") == null ? "" : sessionStorage.getItem("email");
	}

	getInstituteId(): number {
		return sessionStorage.getItem("institute") == null ? -1 : parseInt(sessionStorage.getItem("institute"));
	}

	getInstituteLabel(): string {
		return sessionStorage.getItem("institute_label") == null ? "Privé" : sessionStorage.getItem("institute_label");
	}

	getInstitute(): Institute {
		return { id: this.getInstituteId(), label: this.getInstituteLabel() };
	}

	getUserName(): string {
		return sessionStorage.getItem("firstname") + " " + sessionStorage.getItem("lastname")[0] + ".";
	}

	getFirstName(): string {
		return sessionStorage.getItem("firstname");
	}

	getLastName(): string {
		return sessionStorage.getItem("lastname");
	}

	getUserConfig(): Observable<IUserConfig> {
		return this.httpClient.get<IUserConfig>(this.mitp.Users() + "/config");
	}

	getDepartmentsId(): number[] {
		try {
			const ids = JSON.parse(sessionStorage.getItem("departments"));
			if (ids == null || !Array.isArray(ids)) {
				return [];
			}
			return ids.map((id) => Number(id)).filter((id) => !isNaN(id));
		} catch {
			return [];
		}
	}

	getDepartmentsIdAdmin(): number[] {
		try {
			const roles = JSON.parse(sessionStorage.getItem("roles"));
			if (roles == null || !Array.isArray(roles)) {
				return [];
			}
			const result = roles
				.filter((role) => role.startsWith("ROLE_ADMIN_USERS_DEPARTMENT_"))
				.map((role) => Number(role.replace("ROLE_ADMIN_USERS_DEPARTMENT_", "")))
				.filter((id) => !isNaN(id));
			return result;
		} catch {
			return [];
		}
	}

	async fetchUserConfig(): Promise<void> {
		const config = await lastValueFrom(this.getUserConfig());
		this.setAuthorView(config.authorView);
		this.setRibbonCollapsed(config.ribbonCollapsed ?? false);
		if (config.language.trim()) {
			this.translate.changeLanguage(config.language);
		}
	}

	getUserSectionTags(): Tag[] {
		try {
			const data = sessionStorage.getItem("user_config");
			if (!data) {
				return [];
			}
			const config = JSON.parse(data) as IUserConfig;
			return config.tags;
		} catch {
			return [];
		}
	}

	setAuthorView(view: EAuthorProtocolsView): void {
		sessionStorage.setItem("authorView", view.toString());
	}

	getAuthorView(): EAuthorProtocolsView {
		return sessionStorage.getItem("authorView") == null
			? EAuthorProtocolsView.LIST
			: parseInt(sessionStorage.getItem("authorView"));
	}

	updateAuthorView(view: EAuthorProtocolsView): Observable<HttpResponse<null>> {
		this.setAuthorView(view);
		return this.httpClient.put<null>(
			this.mitp.Users() + `/config/author-view/${view}`,
			{ authorView: view },
			{ observe: "response" }
		);
	}

	setRibbonCollapsed(collapsed: boolean): void {
		sessionStorage.setItem("ribbonCollapsed", String(collapsed));
		this._ribbonCollapsed.set(collapsed);
	}

	updateRibbonCollapsed(collapsed: boolean): Observable<HttpResponse<null>> {
		this.setRibbonCollapsed(collapsed);
		return this.httpClient.put<null>(this.mitp.Users() + `/config/ribbon-collapsed/${collapsed}`, null, {
			observe: "response",
		});
	}

	createUser(user: any) {
		return this.httpClient.post(this.mitp.Users(), user, {
			observe: "response",
		});
	}

	getUsers() {
		return this.httpClient.get<User[]>(this.mitp.Users());
	}

	getUsersByInstitute() {
		return this.httpClient.get<User[]>(this.mitp.Institutes() + "/users");
	}

	getUserByEmail(email: string) {
		return this.httpClient.get<User>(this.mitp.Users() + `/search?email=${email}`);
	}

	updateUser(user: User) {
		return this.httpClient.put(this.mitp.Users() + `/${user.id}`, user, {
			observe: "response",
		});
	}

	updateUserStatus(userId: number, status: string) {
		return this.httpClient.put(this.mitp.Users() + `/${userId}/status/${status}`, null, { observe: "response" });
	}

	deleteUser(userId: number) {
		return this.httpClient.delete(this.mitp.Users() + `/${userId}`, {
			observe: "response",
		});
	}

	setUserManager(userId: number, value: boolean) {
		const url = this.mitp.Users() + `/${userId}/roles/manager`;
		if (value) {
			return this.httpClient.post<string[]>(url, null, {
				observe: "response",
			});
		} else {
			return this.httpClient.delete<string[]>(url, {
				observe: "response",
			});
		}
	}

	setUserPathwaysManager(userId: number, value: boolean) {
		const url = this.mitp.Users() + `/${userId}/roles/pathways-manager`;
		if (value) {
			return this.httpClient.post<string[]>(url, null, {
				observe: "response",
			});
		} else {
			return this.httpClient.delete<string[]>(url, {
				observe: "response",
			});
		}
	}

	setUserQuality(userId: number, value: boolean) {
		const url = this.mitp.Users() + `/${userId}/roles/quality`;
		if (value) {
			return this.httpClient.post<string[]>(url, null, {
				observe: "response",
			});
		} else {
			return this.httpClient.delete<string[]>(url, {
				observe: "response",
			});
		}
	}

	setUserCoderRole(userId: number, value: boolean) {
		const url = this.mitp.Users() + `/${userId}/roles/coder`;
		if (value) {
			return this.httpClient.post<string[]>(url, null, {
				observe: "response",
			});
		} else {
			return this.httpClient.delete<string[]>(url, {
				observe: "response",
			});
		}
	}

	setUserHelpAuthorRole(userId: number, value: boolean) {
		const url = this.mitp.Users() + `/${userId}/roles/help-author`;
		if (value) {
			return this.httpClient.post<string[]>(url, null, {
				observe: "response",
			});
		} else {
			return this.httpClient.delete<string[]>(url, {
				observe: "response",
			});
		}
	}

	setUserEducationManager(userId: number, value: boolean) {
		const url = this.mitp.Users() + `/${userId}/roles/education-manager`;
		if (value) {
			return this.httpClient.post<string[]>(url, null, {
				observe: "response",
			});
		} else {
			return this.httpClient.delete<string[]>(url, {
				observe: "response",
			});
		}
	}

	setUserAdminDirectory(userId: number, value: boolean) {
		const url = this.mitp.Users() + `/${userId}/roles/admin-directory`;
		if (value) {
			return this.httpClient.post<string[]>(url, null, {
				observe: "response",
			});
		} else {
			return this.httpClient.delete<string[]>(url, {
				observe: "response",
			});
		}
	}

	setUserAdminUsers(userId: number, value: boolean, type?: string) {
		if (value) {
			return this.httpClient.post<string[]>(this.mitp.Users() + `/${userId}/roles/${type}`, null, {
				observe: "response",
			});
		} else {
			return this.httpClient.delete<string[]>(this.mitp.Users() + `/${userId}/roles/admin-users`, {
				observe: "response",
			});
		}
	}

	setUserInstituteRole(userId: number, instituteId: number, value: boolean) {
		const url = this.mitp.Users() + `/${userId}/admin-users/institutes/${instituteId}`;
		if (value) {
			return this.httpClient.post(url, null, { observe: "response" });
		} else {
			return this.httpClient.delete(url, { observe: "response" });
		}
	}

	setUserDepartmentRole(userId: number, dptId: number, value: boolean) {
		const url = this.mitp.Users() + `/${userId}/admin-users/departments/${dptId}`;
		if (value) {
			return this.httpClient.post(url, null, { observe: "response" });
		} else {
			return this.httpClient.delete(url, { observe: "response" });
		}
	}

	resetPassword(userId: number, type?: string) {
		let url = this.mitp.Users() + `/${userId}/reset-password`;
		if (type != null) {
			url += `?type=${type}`;
		}
		return this.httpClient.put(url, null, { observe: "response" });
	}

	resetPasswordByEmail(userMail: string) {
		return this.httpClient.post(this.mitp.Login() + "/reset-password", {
			email: userMail,
		});
	}

	getUserProfil() {
		return this.httpClient.get(this.mitp.Users() + "/my");
	}

	setUserPassword(pwds: any) {
		return this.httpClient.put(this.mitp.Users() + `/change-password`, pwds, {
			observe: "response",
			context: new HttpContext().set(SKIP_ERROR_TOAST, true),
		});
	}

	/**
	 * The message to show when changing or resetting a password fails. A rejected password gets our own
	 * wording, since the rules shown to the user are all met by then; anything else keeps the backend's.
	 */
	passwordChangeErrorMessage(error: HttpErrorResponse): string {
		if (error.error?.key === PASSWORD_NOT_SAFE_KEY) {
			return this.translate.instant("auth.password.not-safe");
		}
		return error.error?.message ?? this.translate.instant("auth.reset-password.error");
	}

	updateTheme(theme: string): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(this.mitp.Users() + "/config/themes/" + theme, null, { observe: "response" });
	}

	updateLang(lang: string): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(this.mitp.Users() + "/config/langs/" + lang, null, { observe: "response" });
	}

	completeOnboarding(key: string): Observable<HttpResponse<null>> {
		return this.httpClient.put<null>(this.mitp.Users() + "/config/onboarding/" + key, null, {
			observe: "response",
		});
	}

	resetOnboarding(key: string): Observable<HttpResponse<null>> {
		return this.httpClient.delete<null>(this.mitp.Users() + "/config/onboarding/" + key, { observe: "response" });
	}

	createAuthCode(dateLimit?: Date, countLimit?: number): Observable<AuthCode> {
		const content = {};
		if (dateLimit != null) {
			content["dateLimit"] = dateLimit;
		}
		if (countLimit != null) {
			content["countLimit"] = countLimit;
		}
		return this.httpClient.post<AuthCode>(this.mitp.Signup() + "/auth-codes", content);
	}

	signup(user: PublicUser): Observable<null> {
		return this.httpClient.post<null>(this.mitp.Signup(), user);
	}

	publicResetPassword(token: string, pwd: string): Observable<HttpResponse<null>> {
		return this.httpClient.post<null>(
			this.mitp.Login() + `/reset-password/reset/${token}`,
			{ password: pwd },
			{ observe: "response" }
		);
	}
}

export interface PasskeyCreationOptionsResponse {
	status: string;
	errorMessage?: string;
	rp: {
		name: string;
		id?: string;
	};
	user: {
		id: string;
		name: string;
		displayName: string;
	};
	challenge: string;
	pubKeyCredParams: Array<{ type: PublicKeyCredentialType; alg: number }>;
	timeout?: number;
	authenticatorSelection?: AuthenticatorSelectionCriteria;
	excludeCredentials?: Array<{
		type: PublicKeyCredentialType;
		id: string;
		transports?: AuthenticatorTransport[];
	}>;
	attestation?: AttestationConveyancePreference;
	extensions?: AuthenticationExtensionsClientInputs;
}

export interface PasskeyRegistrationPayload {
	id: string;
	rawId: string;
	type: PublicKeyCredentialType;
	response: {
		clientDataJSON: string;
		attestationObject: string;
	};
	clientExtensionResults?: AuthenticationExtensionsClientOutputs;
}
